/**
 * DocCatalogTab — Pulse Doc Catalog admin panel (31/8).
 * Priority order (owner): (1) Estimate Proposals confirm/reject (§6 flow),
 * (2) documents list with re-ingest/delete/download, (3) upload form.
 * Display always prefers source_org_label over the enum ('Phocuswright',
 * never 'Other'); delete warns that chunks leave the Ask the Data corpus.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { TabsContent } from "../ui/tabs";
import {
  FileText, RefreshCw, Trash2, Download, Upload, Check, X,
  ExternalLink, FlaskConical, Link2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { useAuth } from "../../AuthContext";

const SOURCE_ORGS = [
  { value: "WTTC", label: "WTTC" },
  { value: "OECD", label: "OECD" },
  { value: "UN_TOURISM", label: "UN Tourism" },
  { value: "OTHER", label: "Other (set the real organisation name below)" },
];
const ENUM_LABELS = { WTTC: "WTTC", OECD: "OECD", UN_TOURISM: "UN Tourism", OTHER: "Other" };
const DOC_TYPES = ["report", "factsheet", "methodology"];
const ACCEPT = ".pdf,.docx,.xlsx,.pptx,.odt,.ods,.odp,.rtf,.epub";
const AUTO_REFRESH_MS = 8000;   // poll while any doc is in background OCR

export const orgLabel = (d) =>
  (d.source_org_label || "").trim() || ENUM_LABELS[d.source_org] || d.source_org || "—";

const EMPTY_FORM = {
  title: "", source_org: "OTHER", source_org_label: "", doc_type: "report",
  economy: "", publication_year: "", official_url: "", license_note: "", lang: "en",
};

export default function DocCatalogTab({ dm }) {
  const { authHeader } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selProps, setSelProps] = useState(new Set());   // bulk selection (proposals)
  const [selDocs, setSelDocs] = useState(new Set());     // bulk selection (file docs)
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, d] = await Promise.all([
        axios.get(`${API}/admin/pulse/estimate-proposals?status=proposed`, { headers: authHeader() }),
        axios.get(`${API}/admin/pulse/documents`, { headers: authHeader() }),
      ]);
      setProposals(p.data.proposals || []);
      setDocs(d.data.documents || []);
      setSelProps(new Set());
      setSelDocs(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load the Doc Catalog");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh: silent poll (no spinner, selections kept) while OCR runs in background.
  const processingIds = docs.filter(d => d.status === "processing").map(d => d.id);
  const processingCount = processingIds.length;
  const docsRef = useRef(docs);
  docsRef.current = docs;

  const silentRefresh = useCallback(async () => {
    if (document.visibilityState === "hidden") return;
    try {
      const { data } = await axios.get(`${API}/admin/pulse/documents`, { headers: authHeader() });
      const next = data.documents || [];
      const finished = docsRef.current.filter(prev =>
        prev.status === "processing" &&
        next.some(n => n.id === prev.id && n.status !== "processing"));
      setDocs(next);
      if (finished.length) {
        finished.forEach(prev => {
          const n = next.find(x => x.id === prev.id);
          if (n.status === "ingested") toast.success(`OCR complete — "${n.title}" → ${n.chunk_count} chunks`);
          else toast.error(`OCR failed — "${n.title}": ${n.error || "unknown error"}`);
        });
        const p = await axios.get(`${API}/admin/pulse/estimate-proposals?status=proposed`, { headers: authHeader() });
        setProposals(p.data.proposals || []);
      }
    } catch { /* transient — next tick retries */ }
  }, [authHeader]);

  useEffect(() => {
    if (!processingCount) return undefined;
    const id = setInterval(silentRefresh, AUTO_REFRESH_MS);
    document.addEventListener("visibilitychange", silentRefresh);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", silentRefresh);
    };
  }, [processingCount, silentRefresh]);

  const decide = async (proposal, action) => {
    setActing(proposal.id);
    try {
      await axios.post(
        `${API}/admin/pulse/estimate-proposals/${proposal.id}/${action}`,
        {}, { headers: authHeader() });
      setProposals(prev => prev.filter(p => p.id !== proposal.id));
      toast.success(action === "confirm"
        ? `Confirmed — "${proposal.metric}" is now a curated estimate in Ask the Data`
        : "Proposal rejected");
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} the proposal`);
    } finally {
      setActing(null);
    }
  };

  const reingest = async (doc) => {
    setActing(doc.id);
    try {
      const { data } = await axios.post(
        `${API}/admin/pulse/documents/${doc.id}/reingest`, {}, { headers: authHeader() });
      if (data.status === "processing") {
        toast.success(`Scanned pages detected — OCR running in background (${data.ocr_pages} pages). The list auto-refreshes.`);
      } else {
        toast.success(data.status === "ingested"
          ? `Re-ingested — ${data.chunk_count} chunks`
          : `Re-ingest failed: ${data.error || "unknown error"}`);
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-ingest failed");
    } finally {
      setActing(null);
    }
  };

  const removeDoc = async (doc) => {
    const warn =
      `Delete "${doc.title}"?\n\n` +
      `⚠ This ALSO removes its ${doc.chunk_count || 0} chunk(s) from the Ask the Data ` +
      `corpus — chat retrieval stops citing this document immediately.\n\nThis cannot be undone.`;
    if (!window.confirm(warn)) return;
    setActing(doc.id);
    try {
      await axios.delete(`${API}/admin/pulse/documents/${doc.id}`, { headers: authHeader() });
      setDocs(prev => prev.filter(d => d.id !== doc.id));
      setProposals(prev => prev.filter(p => p.doc_id !== doc.id));
      toast.success("Document, file and corpus chunks deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setActing(null);
    }
  };

  const download = async (doc) => {
    setActing(doc.id);
    try {
      const res = await axios.get(`${API}/admin/pulse/documents/${doc.id}/file`,
        { headers: authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || `${doc.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    } finally {
      setActing(null);
    }
  };

  const toggleIn = (setter) => (id) => setter(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleProp = toggleIn(setSelProps);
  const toggleDoc = toggleIn(setSelDocs);

  const bulkDecide = async (action) => {
    const ids = [...selProps];
    if (!ids.length) return;
    if (action === "reject" && !window.confirm(`Reject ${ids.length} proposal(s)? They will be discarded.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/pulse/estimate-proposals/bulk`,
        { ids, action }, { headers: authHeader() });
      const ok = (data.done || []).length, ko = (data.failed || []).length;
      toast[ko ? "error" : "success"](
        `${ok} proposal(s) ${action === "confirm" ? "confirmed as curated estimates" : "rejected"}${ko ? ` — ${ko} failed` : ""}`);
      setProposals(prev => prev.filter(p => !(data.done || []).includes(p.id)));
      setSelProps(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || `Bulk ${action} failed`);
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkReingest = async () => {
    const ids = [...selDocs];
    if (!ids.length) return;
    const warn =
      `Re-ingest ${ids.length} document(s)?\n\n` +
      "Scanned PDFs will switch to 'processing' and run OCR in the background " +
      "(one-time Claude cost per scanned page, max 2 documents at a time).";
    if (!window.confirm(warn)) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/pulse/documents/bulk-reingest`,
        { ids }, { headers: authHeader() });
      const rs = data.results || [];
      const ing = rs.filter(r => r.status === "ingested").length;
      const proc = rs.filter(r => r.status === "processing").length;
      const bad = rs.length - ing - proc;
      toast[bad ? "error" : "success"](
        `${ing} re-ingested, ${proc} in background OCR${bad ? `, ${bad} failed/skipped` : ""}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk re-ingest failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const upload = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!/^https?:\/\//.test(form.official_url.trim())) {
      toast.error("official_url is required (the rights holder's page)"); return;
    }
    if (!form.license_note.trim()) { toast.error("license_note is required"); return; }
    if (form.source_org === "OTHER" && !form.source_org_label.trim()) {
      toast.error("Organisation name is required when source is Other (e.g. \"Phocuswright\")");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("metadata", JSON.stringify({
        title: form.title.trim(),
        source_org: form.source_org,
        source_org_label: form.source_org_label.trim() || null,
        doc_type: form.doc_type,
        economy: form.economy.trim() || null,
        publication_year: form.publication_year ? Number(form.publication_year) : null,
        official_url: form.official_url.trim(),
        license_note: form.license_note.trim(),
        lang: form.lang,
      }));
      const { data } = await axios.post(`${API}/admin/pulse/documents`, fd,
        { headers: authHeader() });
      if (data.status === "ingested") {
        toast.success(`Ingested — ${data.chunk_count} chunks${data.deduped ? " (replaced existing version)" : ""}. Check for new estimate proposals above.`);
      } else if (data.status === "processing") {
        toast.success(`Scanned pages detected — OCR running in background (${data.ocr_pages} pages). The list auto-refreshes.`);
      } else {
        toast.error(`Upload stored but ingest failed: ${data.error || "unknown error"}`);
      }
      setForm(EMPTY_FORM);
      setFile(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const card = `rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`;
  const cardShadow = { boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" };
  const input = `w-full px-3 py-2 rounded-xl text-sm border-0 outline-none ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-[#f3f4f5] text-[#191c1d] placeholder:text-[#bac9cc]"}`;
  const lbl = `text-xs font-medium block mb-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`;
  const pill = (color) => `px-2 py-0.5 rounded-full text-[9px] font-mono font-semibold ${
    color === "green" ? (dm ? "bg-emerald-400/15 text-emerald-300" : "bg-emerald-100 text-emerald-700") :
    color === "red"   ? (dm ? "bg-rose-400/15 text-rose-300"       : "bg-rose-100 text-rose-700")       :
    color === "amber" ? (dm ? "bg-amber-400/15 text-amber-300"     : "bg-amber-100 text-amber-700")     :
    color === "sky"   ? (dm ? "bg-sky-400/15 text-sky-300"         : "bg-sky-100 text-sky-700")         :
                        (dm ? "bg-slate-700 text-slate-400"        : "bg-slate-200 text-slate-500")
  }`;
  const iconBtn = `p-2 rounded-lg transition-colors disabled:opacity-40 ${dm ? "hover:bg-white/5 text-slate-400" : "hover:bg-black/5 text-[#6b7a7d]"}`;

  return (
    <TabsContent value="doc-catalog">
      <div className="max-w-3xl space-y-6" data-testid="doc-catalog-tab">

        <div>
          <h2 className={`editorial-heading text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
            Pulse Doc Catalog
          </h2>
          <p className={`text-sm mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Governed documents feeding Ask the Data. Files stay admin-only (hard copyright rule) —
            chat only ever cites title, source, year and the official link.
          </p>
        </div>

        {/* 1 — Estimate Proposals (§6) */}
        <div className={card} style={cardShadow} data-testid="estimate-proposals-card">
          <h3 className={`text-sm font-semibold mb-1 flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
            <FlaskConical className="w-4 h-4" /> Estimate Proposals
            {!loading && <span className={`ml-auto ${pill("slate")}`}>{proposals.length} pending</span>}
          </h3>
          <p className={`text-[11px] mb-4 leading-snug ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Headline figures the LLM spotted in ingested documents. Confirm → curated estimate
            (preferred by chat over raw text, always introduced as a modeled estimate). Reject → discarded.
          </p>
          {!loading && proposals.length > 0 && (
            <div className="flex items-center flex-wrap gap-2 mb-3">
              <label className={`flex items-center gap-2 text-xs cursor-pointer ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                <input type="checkbox" data-testid="proposals-select-all"
                  checked={selProps.size === proposals.length}
                  onChange={e => setSelProps(e.target.checked ? new Set(proposals.map(p => p.id)) : new Set())}
                  className={`w-4 h-4 rounded ${dm ? "accent-cyan-400" : "accent-[#006874]"}`} />
                Select all
              </label>
              {selProps.size > 0 && (
                <>
                  <button onClick={() => bulkDecide("confirm")} disabled={bulkBusy}
                    data-testid="proposals-bulk-confirm"
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors disabled:opacity-50 ${dm ? "bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400/25" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}`}>
                    <Check className="w-3 h-3" /> Confirm {selProps.size}
                  </button>
                  <button onClick={() => bulkDecide("reject")} disabled={bulkBusy}
                    data-testid="proposals-bulk-reject"
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors disabled:opacity-50 ${dm ? "bg-rose-400/15 text-rose-300 hover:bg-rose-400/25" : "bg-rose-100 text-rose-700 hover:bg-rose-200"}`}>
                    <X className="w-3 h-3" /> Reject {selProps.size}
                  </button>
                </>
              )}
            </div>
          )}
          {loading ? (
            <div className="flex justify-center py-6"><RefreshCw className={`w-4 h-4 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} /></div>
          ) : proposals.length === 0 ? (
            <p className={`text-sm py-2 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`} data-testid="proposals-empty">
              No pending proposals — new ones appear automatically after each document ingest.
            </p>
          ) : (
            <div className="space-y-2">
              {proposals.map(p => (
                <div key={p.id} data-testid={`proposal-row-${p.id}`}
                  className={`flex items-start gap-3 p-3 rounded-xl ${dm ? "bg-[#0c1011]" : "bg-[#f3f4f5]"}`}>
                  <input type="checkbox" data-testid={`proposal-check-${p.id}`}
                    checked={selProps.has(p.id)} onChange={() => toggleProp(p.id)}
                    className={`mt-1 w-4 h-4 rounded flex-shrink-0 ${dm ? "accent-cyan-400" : "accent-[#006874]"}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                      {p.economy ? `${p.economy} — ` : ""}{p.metric}
                    </p>
                    <p className={`text-xs mt-0.5 ${dm ? "text-slate-400" : "text-[#3b494c]"}`}>
                      {p.year}: <strong>{Number(p.value).toLocaleString()}</strong> {p.unit}
                    </p>
                    <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                      <span className={pill("sky")} data-testid={`proposal-source-${p.id}`}>{orgLabel(p)}</span>
                      {p.is_forecast && <span className={pill("amber")}>forecast</span>}
                      <span className={`text-[10px] font-mono ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{p.doc_id}</span>
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button onClick={() => decide(p, "confirm")} disabled={acting === p.id}
                      data-testid={`proposal-confirm-${p.id}`} title="Confirm as curated estimate"
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors disabled:opacity-50 ${dm ? "bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400/25" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}`}>
                      <Check className="w-3 h-3" /> Confirm
                    </button>
                    <button onClick={() => decide(p, "reject")} disabled={acting === p.id}
                      data-testid={`proposal-reject-${p.id}`} title="Reject proposal"
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors disabled:opacity-50 ${dm ? "bg-rose-400/15 text-rose-300 hover:bg-rose-400/25" : "bg-rose-100 text-rose-700 hover:bg-rose-200"}`}>
                      <X className="w-3 h-3" /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 2 — Documents list */}
        <div className={card} style={cardShadow} data-testid="documents-card">
          <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
            <FileText className="w-4 h-4" /> Documents
            {!loading && <span className={`ml-auto ${pill("slate")}`}>{docs.length} in catalog</span>}
            {!loading && processingCount > 0 && (
              <span className={`${pill("amber")} flex items-center gap-1`} data-testid="docs-auto-refresh-indicator"
                title={`Auto-refresh every ${AUTO_REFRESH_MS / 1000}s while OCR runs in the background`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                auto-refresh · {processingCount} in OCR
              </span>
            )}
            <button onClick={load} disabled={loading} data-testid="docs-refresh-btn"
              title="Refresh list (OCR ingests finish in the background)"
              className={iconBtn}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </h3>
          {!loading && docs.some(d => d.kind === "file") && (
            <div className="flex items-center flex-wrap gap-2 mb-3">
              <label className={`flex items-center gap-2 text-xs cursor-pointer ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                <input type="checkbox" data-testid="docs-select-all"
                  checked={selDocs.size === docs.filter(d => d.kind === "file").length && selDocs.size > 0}
                  onChange={e => setSelDocs(e.target.checked
                    ? new Set(docs.filter(d => d.kind === "file").map(d => d.id)) : new Set())}
                  className={`w-4 h-4 rounded ${dm ? "accent-cyan-400" : "accent-[#006874]"}`} />
                Select all files
              </label>
              {selDocs.size > 0 && (
                <button onClick={bulkReingest} disabled={bulkBusy}
                  data-testid="docs-bulk-reingest"
                  title="Re-ingest selected — scanned PDFs run OCR in the background"
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors disabled:opacity-50 ${dm ? "bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25" : "bg-[#006874]/10 text-[#006874] hover:bg-[#006874]/20"}`}>
                  <RefreshCw className={`w-3 h-3 ${bulkBusy ? "animate-spin" : ""}`} /> Re-ingest {selDocs.size}
                </button>
              )}
            </div>
          )}
          {loading ? (
            <div className="flex justify-center py-6"><RefreshCw className={`w-4 h-4 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} /></div>
          ) : docs.length === 0 ? (
            <p className={`text-sm py-2 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`} data-testid="docs-empty">
              No documents yet — upload below or push from Mission Control.
            </p>
          ) : (
            <div className="space-y-2">
              {docs.map(d => (
                <div key={d.id} data-testid={`doc-row-${d.id}`}
                  className={`flex items-start gap-3 p-3 rounded-xl ${dm ? "bg-[#0c1011]" : "bg-[#f3f4f5]"}`}>
                  {d.kind === "file" ? (
                    <input type="checkbox" data-testid={`doc-check-${d.id}`}
                      checked={selDocs.has(d.id)} onChange={() => toggleDoc(d.id)}
                      className={`mt-1 w-4 h-4 rounded flex-shrink-0 ${dm ? "accent-cyan-400" : "accent-[#006874]"}`} />
                  ) : (
                    <span className="w-4 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`} title={d.title}>
                      {d.title}
                    </p>
                    <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                      <span className={pill("sky")} title={`source_org: ${d.source_org}`}
                        data-testid={`doc-source-${d.id}`}>{orgLabel(d)}</span>
                      <span className={pill("slate")}>{d.doc_type}</span>
                      {d.publication_year && <span className={pill("slate")}>{d.publication_year}</span>}
                      {d.economy && <span className={pill("slate")}>{d.economy}</span>}
                      <span className={pill(d.status === "ingested" ? "green" : d.status === "failed" ? "red" : d.status === "processing" ? "amber" : "slate")}
                        data-testid={`doc-status-${d.id}`}>{d.status}</span>
                      <span className={pill("slate")}>{d.chunk_count || 0} chunks</span>
                      {d.kind === "url" && <span className={pill("amber")} title="URL catalog entry — no file">url</span>}
                    </div>
                    {d.status === "processing" && (
                      <p className={`text-[11px] mt-1 ${dm ? "text-amber-400" : "text-amber-600"}`}
                        data-testid={`doc-ocr-note-${d.id}`}>
                        OCR running in background{d.ocr_pages ? ` (${d.ocr_pages} scanned pages)` : ""} — this list refreshes automatically
                      </p>
                    )}
                    {d.error && (
                      <p className={`text-[11px] mt-1 ${dm ? "text-rose-400" : "text-rose-600"}`}>{d.error}</p>
                    )}
                    <a href={d.official_url} target="_blank" rel="noreferrer"
                      data-testid={`doc-official-url-${d.id}`}
                      className={`inline-flex items-center gap-1 text-[11px] mt-1 hover:underline ${dm ? "text-cyan-400" : "text-[#006874]"}`}>
                      <Link2 className="w-3 h-3" /> official source
                    </a>
                  </div>
                  <div className="flex gap-0.5 flex-shrink-0">
                    {d.kind === "file" && (
                      <>
                        <button onClick={() => reingest(d)} disabled={acting === d.id}
                          data-testid={`doc-reingest-${d.id}`} title="Re-ingest (re-parse + re-embed)"
                          className={iconBtn}>
                          <RefreshCw className={`w-4 h-4 ${acting === d.id ? "animate-spin" : ""}`} />
                        </button>
                        <button onClick={() => download(d)} disabled={acting === d.id}
                          data-testid={`doc-download-${d.id}`} title="Download original file (admin only)"
                          className={iconBtn}>
                          <Download className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button onClick={() => removeDoc(d)} disabled={acting === d.id}
                      data-testid={`doc-delete-${d.id}`}
                      title="Delete — also removes its chunks from the Ask the Data corpus"
                      className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${dm ? "hover:bg-rose-400/10 text-rose-400" : "hover:bg-rose-50 text-rose-600"}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 3 — Upload form */}
        <div className={card} style={cardShadow} data-testid="upload-card">
          <h3 className={`text-sm font-semibold mb-1 flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
            <Upload className="w-4 h-4" /> Upload document
          </h3>
          <p className={`text-[11px] mb-4 leading-snug ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            PDF/Office up to 30MB. Ingest is anydoc-first (on-device, no credits); chunks join the
            Ask the Data corpus and estimate proposals are generated automatically.
          </p>
          <div className="space-y-3">
            <div>
              <label className={lbl}>File *</label>
              <input type="file" accept={ACCEPT} data-testid="upload-file-input"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className={`${input} file:mr-3 file:px-3 file:py-1 file:rounded-lg file:border-0 file:text-xs ${dm ? "file:bg-[#1a2122] file:text-slate-300" : "file:bg-white file:text-[#3b494c]"}`} />
            </div>
            <div>
              <label className={lbl}>Title *</label>
              <input type="text" value={form.title} data-testid="upload-title-input"
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder='e.g. "Travel & Tourism Economic Impact 2026 — Italy"' className={input} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Source (classification)</label>
                <select value={form.source_org} data-testid="upload-source-org-select"
                  onChange={e => setForm(f => ({ ...f, source_org: e.target.value }))} className={input}>
                  {SOURCE_ORGS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>
                  Organisation name {form.source_org === "OTHER" ? "*" : "(optional)"}
                </label>
                <input type="text" value={form.source_org_label} data-testid="upload-source-org-label-input"
                  onChange={e => setForm(f => ({ ...f, source_org_label: e.target.value }))}
                  placeholder='e.g. "Phocuswright", "IATA", "WEF"' className={input} />
                <p className={`text-[10px] mt-1 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                  Shown in citations and lists — never &quot;Other&quot;.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={lbl}>Type</label>
                <select value={form.doc_type} data-testid="upload-doc-type-select"
                  onChange={e => setForm(f => ({ ...f, doc_type: e.target.value }))} className={input}>
                  {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Economy</label>
                <input type="text" value={form.economy} data-testid="upload-economy-input"
                  onChange={e => setForm(f => ({ ...f, economy: e.target.value }))}
                  placeholder='e.g. "Italy"' className={input} />
              </div>
              <div>
                <label className={lbl}>Year</label>
                <input type="number" value={form.publication_year} data-testid="upload-year-input"
                  onChange={e => setForm(f => ({ ...f, publication_year: e.target.value }))}
                  placeholder="2026" min="1990" max="2100" className={input} />
              </div>
            </div>
            <div>
              <label className={lbl}>Official URL * <span className="font-normal">(the rights holder&apos;s page)</span></label>
              <input type="url" value={form.official_url} data-testid="upload-official-url-input"
                onChange={e => setForm(f => ({ ...f, official_url: e.target.value }))}
                placeholder="https://…" className={input} />
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <label className={lbl}>License note *</label>
                <input type="text" value={form.license_note} data-testid="upload-license-note-input"
                  onChange={e => setForm(f => ({ ...f, license_note: e.target.value }))}
                  placeholder='e.g. "Member content — internal analysis only, cite the official page"'
                  className={input} />
              </div>
              <div>
                <label className={lbl}>Lang</label>
                <select value={form.lang} data-testid="upload-lang-select"
                  onChange={e => setForm(f => ({ ...f, lang: e.target.value }))} className={input}>
                  <option value="en">EN</option>
                  <option value="it">IT</option>
                </select>
              </div>
            </div>
            <button onClick={upload} disabled={uploading} data-testid="upload-submit-btn"
              className={`w-full px-4 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}>
              {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Ingesting (parse + embed + proposals)…" : "Upload & ingest"}
            </button>
          </div>
        </div>
      </div>
    </TabsContent>
  );
}
